import { logActivity } from '../utils/activityLogger';

export class AppUpdateService {
  private static readonly VERSION_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes
  private updateCheckInterval: NodeJS.Timeout | null = null;
  private isUpdateAvailable = false;
  private updateCallbacks: Array<() => void> = [];
  private static instance: AppUpdateService;
  private currentVersion: string = '';

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): AppUpdateService {
    if (!AppUpdateService.instance) {
      AppUpdateService.instance = new AppUpdateService();
    }
    return AppUpdateService.instance;
  }

  public initialize(currentVersion: string): void {
    this.currentVersion = currentVersion;
    this.checkForUpdates();
    this.startPeriodicCheck();
    this.setupUpdateListeners();
  }

  public onUpdateAvailable(callback: () => void): () => void {
    this.updateCallbacks.push(callback);
    return () => {
      this.updateCallbacks = this.updateCallbacks.filter(cb => cb !== callback);
    };
  }

  private async checkForUpdates(): Promise<void> {
    try {
      const response = await fetch('/version.json');
      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }
      
      const data = await response.json();
      const latestVersion = data.version;
      
      if (this.isNewerVersion(this.currentVersion, latestVersion)) {
        this.handleUpdateAvailable(latestVersion);
      } else {
        console.log('App is up to date');
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }

  private isNewerVersion(current: string, latest: string): boolean {
    const currentParts = current.split(/[.-]/).map(Number);
    const latestParts = latest.split(/[.-]/).map(Number);

    for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
      const currentPart = currentParts[i] || 0;
      const latestPart = latestParts[i] || 0;

      if (latestPart > currentPart) return true;
      if (latestPart < currentPart) return false;
    }

    return false;
  }

  private handleUpdateAvailable(version: string): void {
    if (this.isUpdateAvailable) return;
    
    this.isUpdateAvailable = true;
    
    // Notify subscribers
    this.notifyUpdateAvailable(version);
    
    // Log the update availability
    logActivity(
      'INFO',
      `Update available: ${version}`,
      { currentVersion: this.currentVersion, newVersion: version }
    );
  }

  private notifyUpdateAvailable(version: string): void {
    // Dispatch a custom event that the UI can listen to
    const event = new CustomEvent('app-update-available', { 
      detail: { version } 
    });
    window.dispatchEvent(event);
    
    // Notify subscribers
    this.updateCallbacks.forEach(callback => callback());
  }

  public applyUpdate(): void {
    if ('serviceWorker' in navigator) {
      // Tell the service worker to skip waiting and activate the new version
      navigator.serviceWorker.ready.then(registration => {
        registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
      });

      // Reload the page when the new service worker takes control
      let refreshing = false;
      const handleControllerChange = () => {
        if (!refreshing) {
          window.location.reload();
          refreshing = true;
        }
      };
      
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    } else {
      // Fallback for browsers without service workers
      window.location.reload();
    }
  }

  private startPeriodicCheck(): void {
    // Clear any existing interval
    this.stopPeriodicCheck();
    
    // Set up new interval
    this.updateCheckInterval = setInterval(
      () => this.checkForUpdates(),
      AppUpdateService.VERSION_CHECK_INTERVAL
    );
  }

  private stopPeriodicCheck(): void {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = null;
    }
  }

  private setupUpdateListeners(): void {
    if (!('serviceWorker' in navigator)) return;
    
    // Listen for controller change (when a new service worker takes over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    // Listen for messages from the service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NEW_VERSION_AVAILABLE') {
        this.handleUpdateAvailable(event.data.version);
      }
    });
  }

  public destroy(): void {
    this.stopPeriodicCheck();
    this.updateCallbacks = [];
    
    // Clean up event listeners
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', () => {});
      navigator.serviceWorker.removeEventListener('controllerchange', () => {});
    }
  }
}

export const appUpdateService = AppUpdateService.getInstance();
